import { OktaAuth } from '@okta/okta-auth-js';
import { PayloadAction, createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { OKTA_DEVICE_TOKEN_KEY, OKTA_LOGIN_URL } from '../../common/constants';
import { uipApiInstance } from '../../services';
import {
    getOrganizationConfig,
    getTenantBasedConfig,
    logEventToBackEnd,
    updateGlobalLoaderState,
    fetchGoeConfig,
    fetchPortfolio,
    fetchNotifications
} from '../global/globalSlice';
import i18n from '../../i18n-backend';
import { ADVISOR_ROUTES } from '../../routes/routeConstants';
import { navigateTo } from '../../common/helper';

type InitialState = {
    isAuthenticated: boolean;
    user: any;
    loading: boolean;
    isSendingMFA: boolean;
    error?: null | undefined | any;
    showSessionExpiredModal: boolean;
    sendingForgotPassword?: boolean;
    isHantzUserSettings: boolean;
    oktaState: any;
    languageChangedBeforeLogin: boolean;
};

const initialState: InitialState = {
    isAuthenticated: false,
    user: {},
    loading: false,
    isSendingMFA: false,
    error: null,
    showSessionExpiredModal: false,
    sendingForgotPassword: false,
    isHantzUserSettings: false,
    oktaState: {},
    languageChangedBeforeLogin: false
};

/** Function to addLogEvent */
export const addLogEvent = async (
    apiURL: string,
    requestType: 'request' | 'response',
    reqBody: object | unknown,
    status?: string
) => {
    let data: any = { api_url: apiURL, request_type: requestType, api_request: reqBody };
    if (requestType === 'response' && status) data = { status, ...data };
    else data = { status: '', ...data };
    try {
        await uipApiInstance({
            method: 'POST',
            url: '/api/advice/addLogEvent',
            withCredentials: false,
            data
        });
    } catch (err) {
        console.log(err);
    }
};

const getLoginUserInformation = async (dispatch: any, getState: any) => {
    const state: any = getState();
    const accessToken = state?.auth?.oktaState?.accessToken?.accessToken;
    const oktaState = state?.auth?.oktaState;

    const userInfoRes: any = await axios({
        method: 'GET',
        baseURL: process.env.REACT_APP_UIP_API_URL,
        url: '/api/advice/loginuserinfo',
        withCredentials: false,
        headers: {
            Authorization: 'Bearer ' + accessToken
        }
    });

    const userStatus = userInfoRes?.data?.user_status;
    const activeTenants = Object.keys(userStatus).filter((tnt) => userStatus[tnt]) || [];
    let isMultiTenant = false;
    let tenant = '';
    let userRole = '';
    let isHantzTenant = false;
    let clickWrapAgreements = {};

    isMultiTenant = activeTenants.length > 1;
    tenant = isMultiTenant ? '' : activeTenants[0];
    userRole = isMultiTenant ? '' : userInfoRes?.data.zone_roles?.[tenant]?.role;
    isHantzTenant = isMultiTenant ? false : userRole === 'advisor' && tenant === 'hantz' && userStatus['hantz'];
    clickWrapAgreements = isMultiTenant ? undefined : userInfoRes?.data.cwa[tenant].clickWrapAgreements;

    const getDummyCWA = (userTenants: string[]) => {
        const dummyCWA: any = {};
        userTenants.forEach((tenant: string) => {
            dummyCWA[tenant] = {
                clickWrapAgreements: {}
            };
        });
        return dummyCWA;
    };

    const userInfo = {
        lastLoggedInDateTime: userInfoRes?.data?.lastLogin,
        loggedInUsername: userInfoRes?.data?.name,
        userRole,
        userId: oktaState?.idToken?.claims?.sub,
        userEmail: oktaState?.idToken?.claims?.email,
        accessTokenExpiration: oktaState?.accessToken?.expiresAt,
        tenant,
        tenants: activeTenants,
        freemium: userInfoRes?.data.freemium,
        clickWrapAgreements,
        userStatus,
        zoneRoles: userInfoRes?.data.zone_roles,
        tenantSelected: !isMultiTenant,
        isHantzTenant,
        cwa: userInfoRes?.data.cwa || getDummyCWA(activeTenants || []),
        admin_tenantList: userInfoRes?.data?.admin_tenantList || []
    };

    if (!isMultiTenant) {
        // AEX-3108: fetchPortfolio reads isMultiPortfolioTenant from the tenant config, so wait for both.
        const [, orgInfo] = await Promise.all([
            dispatch(getTenantBasedConfig({ tenantId: tenant || 'advice', i18n, isLoader: true, callCMS: true })),
            dispatch(getOrganizationConfig(tenant))
        ]);
        /** addLogEvent for Successful Authentication by Okta */

        // Get market portfolios and goe config
        if (!isHantzTenant) {
            dispatch(fetchPortfolio(tenant));
            dispatch(fetchGoeConfig({ tenant, clientemail: orgInfo?.payload?.orgInfo?.orgEmail }));
            dispatch(fetchNotifications({ tenant, source: 'SYSTEM' }));
        }
        await addLogEvent(
            OKTA_LOGIN_URL,
            'response',
            {
                userId: userInfo?.userId,
                userRole: userInfo.userRole,
                userName: userInfo.userEmail,
                tenant: userInfo.tenant
            },
            'success'
        );

        const hantz = orgInfo?.payload?.orgInfo?.hantz;
        const userAgreement = orgInfo?.payload?.orgInfo?.userAgreement;
        const eula = orgInfo?.payload?.orgInfo?.eula;
        return { ...userInfo, hantz, userAgreement, eula };
    } else {
        return userInfo;
    }
};

export const getUserRelatedInfo = createAsyncThunk(
    'authState/getUserRelatedInfo',
    async ({ tenant, userRole }: { tenant: string; userRole: string }, { dispatch, rejectWithValue, getState }) => {
        dispatch(updateGlobalLoaderState(true));
        try {
            const state: any = getState();
            // const tenantId = state?.global?.globalConfig?.subdomain;
            const userInfo = state?.auth?.user;

            if (window.heap && userInfo?.userEmail) {
                window.heap?.identify(userInfo?.userEmail);
                window.heap?.addUserProperties({
                    userRole,
                    tenant
                });
            }

            const response = await Promise.all([
                dispatch(getTenantBasedConfig({ tenantId: tenant || 'advice', i18n, isLoader: false, callCMS: true })),
                dispatch(getOrganizationConfig(tenant))
            ]);

            const orgInfo = response[1];

            // Get market portfolios and goe config
            dispatch(fetchPortfolio(tenant));
            dispatch(fetchGoeConfig({ tenant, clientemail: orgInfo?.payload?.orgInfo?.orgEmail }));
            dispatch(fetchNotifications({ tenant, source: 'SYSTEM' }));

            const userStatus = userInfo?.userStatus;
            const isHantzTenant = userRole === 'advisor' && tenant === 'hantz' && userStatus['hantz'];

            const clickWrapAgreements = userInfo?.cwa[tenant]?.clickWrapAgreements || {};

            await addLogEvent(
                OKTA_LOGIN_URL,
                'response',
                {
                    userId: userInfo?.userId,
                    userRole: userRole,
                    userName: userInfo?.userEmail,
                    tenant: tenant
                },
                'success'
            );
            let updatedUserInfo = {
                ...userInfo,
                tenant: tenant,
                userRole: userRole,
                tenantSelected: true,
                isHantzTenant: isHantzTenant,
                clickWrapAgreements: clickWrapAgreements
            };
            const hantz = orgInfo?.payload?.orgInfo?.hantz;
            const userAgreement = orgInfo?.payload?.orgInfo?.userAgreement;
            const eula = orgInfo?.payload?.orgInfo?.eula;
            dispatch(updateGlobalLoaderState(false));
            return { ...updatedUserInfo, hantz, userAgreement, eula };
        } catch (error) {
            dispatch(updateGlobalLoaderState(false));
            console.error('Error in getUserRelatedInfo:', error);
            dispatch(logEventToBackEnd('GET_USER_RELATED_INFO_API_ERROR'));
            return rejectWithValue(error);
        }
    }
);

export const getLoggedInUserInfo = createAsyncThunk(
    'authState/getLoggedInUserInfo',
    async (_, { dispatch, rejectWithValue, getState }) => {
        try {
            let user: any = {};
            user = await getLoginUserInformation(dispatch, getState);

            if (!(user.tenants.length > 1)) {
                if (!user?.userStatus?.[user.tenant]) throw { status: 400 };
                if (window.heap && user?.userEmail) {
                    // Identify user in Heap Analytics across sessions
                    window.heap?.identify(user?.userEmail);
                    window.heap?.addUserProperties({
                        userRole: user?.userRole,
                        tenant: user?.tenant
                    });
                }
            }
            return user;
        } catch (error: any) {
            // dispatch(updateOktaAuthState({ isAuthenticated: false, authState: {} }));
            // oktaAuth.tokenManager.clear();
            // oktaAuth.closeSession();
            // oktaAuth.clearStorage();
            if (error?.status === 400) {
                navigateTo(ADVISOR_ROUTES.ACCESS_DENIED, true);
            }
            dispatch(logEventToBackEnd(`GET_LOGIN_USER_INFO_API_ERROR: ${error}`));
            return rejectWithValue(error);
        }
    }
);

export const updateHantzUserSettings = createAsyncThunk(
    'authState/updateHantzUserSettings',
    async (any, { rejectWithValue, dispatch }) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/update_user_settings',
                withCredentials: false,
                data: {
                    accepted: false,
                    tenant: 'hantz'
                }
            });
            const data = response.status;
            if (data === 200) return true;
        } catch (error) {
            console.error('Error while calling update_user_settings', error);
            dispatch(logEventToBackEnd('UPDATE_USER_SETTINGS_API_ERROR'));
            return rejectWithValue(error);
        }
    }
);

type UserInfo = { oktaAuth: OktaAuth; username: string; password: string };

let transaction: any;
let factor: any;
let deviceTokenFromLocalStorage: any;

export const authenticateUser = createAsyncThunk(
    'authState/authenticateUser',
    async ({ oktaAuth, username, password }: UserInfo, { rejectWithValue, dispatch, getState }) => {
        try {
            // const state: any = getState();

            const stateToken = Math.random().toString(36).substring(2); // Generate random state
            sessionStorage.setItem('oktaState', stateToken); // Store in session

            // const tenantId = state?.global?.globalConfig?.subdomain;

            deviceTokenFromLocalStorage = await localStorage.getItem(OKTA_DEVICE_TOKEN_KEY);
            if (!deviceTokenFromLocalStorage) {
                const uuid = uuidv4();
                localStorage.setItem(OKTA_DEVICE_TOKEN_KEY, uuid.substring(0, 32));
                deviceTokenFromLocalStorage = uuid.substring(0, 32);
            }
            // log authn to backend
            await addLogEvent(OKTA_LOGIN_URL, 'request', {
                username,
                deviceToken: deviceTokenFromLocalStorage || ''
            });
            transaction = await oktaAuth.signInWithCredentials({
                username,
                password,
                ...(deviceTokenFromLocalStorage && {
                    context: {
                        deviceToken: deviceTokenFromLocalStorage || ''
                    }
                }),
                relayState: stateToken // Pass state for verification
            });

            // If status = "MFA_REQUIRED"
            if (transaction?.status === 'MFA_REQUIRED' || transaction?.status === 'MFA_ENROLL') {
                // Send MFA code to preferred MFA option
                factor = transaction?.factors?.find?.(
                    (fac: any) => fac?.provider === 'OKTA' && fac?.factorType === 'email'
                );
                await addLogEvent(
                    OKTA_LOGIN_URL,
                    'response',
                    { factorid: factor?.id, username, deviceToken: deviceTokenFromLocalStorage || '' },
                    'success'
                );
                // eslint-disable-next-line require-atomic-updates
                if (transaction?.status === 'MFA_REQUIRED') {
                    await addLogEvent(OKTA_LOGIN_URL + 'factors/' + factor?.id + '/verify', 'request', {
                        username,
                        deviceToken: deviceTokenFromLocalStorage || ''
                    });
                    transaction = await factor?.verify();
                    if (transaction?.status === 'FAILED' || transaction?.status === 'ERROR')
                        await addLogEvent(
                            OKTA_LOGIN_URL + 'factors/' + factor?.id + '/verify',
                            'response',
                            { username, deviceToken: deviceTokenFromLocalStorage || '', ...transaction },
                            'failed'
                        );
                    else
                        await addLogEvent(
                            OKTA_LOGIN_URL + 'factors/' + factor?.id + '/verify',
                            'response',
                            { username, deviceToken: deviceTokenFromLocalStorage || '' },
                            'success'
                        );
                }

                if (transaction?.status === 'MFA_ENROLL') {
                    transaction = await factor?.enroll();
                }
            }
            let user: any = {};
            if (transaction?.status === 'SUCCESS') {
                user = await getLoginUserInformation(dispatch, getState);
            }

            if (!user) {
                return rejectWithValue('User not allowed to access this application');
            }

            return { status: transaction?.status, user };
        } catch (error) {
            console.log(error);
            // unknow error object type
            await addLogEvent(
                OKTA_LOGIN_URL,
                'response',
                { username, deviceToken: deviceTokenFromLocalStorage || '', error },
                'failed'
            );
            return rejectWithValue(error);
        }
    }
);

export const resendMFA = createAsyncThunk('authState/resendMFA', async (_, { dispatch, rejectWithValue }) => {
    try {
        const resendMFATransaction = await transaction.resend('email');

        transaction.sessionToken = resendMFATransaction.sessionToken;
        return true;
    } catch (error) {
        console.log(error);
        dispatch(logEventToBackEnd('RESEND_MFA_API_ERROR'));
        return rejectWithValue(error);
    }
});

type ConfirmMFAAndLoginType = { oktaAuth: OktaAuth; code: string; rememberDevice?: boolean; username: string };

let mfaTransaction = null;

export const confirmMFAAndLogin = createAsyncThunk(
    'authState/confirmMFA',
    async ({ code, rememberDevice, username }: ConfirmMFAAndLoginType, { rejectWithValue, dispatch, getState }) => {
        try {
            await addLogEvent(
                OKTA_LOGIN_URL + 'factors/' + factor?.id + '/verify?rememberDevice=' + rememberDevice,
                'request',
                { username, deviceToken: deviceTokenFromLocalStorage || '' }
            );
            // const state: any = getState();
            // const tenantId = state?.global?.globalConfig?.subdomain;

            if (transaction.status === 'MFA_ENROLL_ACTIVATE') {
                mfaTransaction = await transaction.activate({
                    passCode: code,
                    rememberDevice
                });
            } else {
                mfaTransaction = await transaction.verify({
                    passCode: code,
                    rememberDevice
                });
            }

            // Reset device token if remember device is not selected
            if (!rememberDevice) {
                localStorage.removeItem(OKTA_DEVICE_TOKEN_KEY);

                const uuid = uuidv4();

                localStorage.setItem(OKTA_DEVICE_TOKEN_KEY, uuid.substring(0, 32));
            }

            transaction.sessionToken = mfaTransaction.sessionToken;

            // log to backend
            if (transaction?.status === 'FAILED' || transaction?.status === 'ERROR')
                await addLogEvent(
                    OKTA_LOGIN_URL + 'factors/' + factor?.id + '/verify?rememberDevice=' + rememberDevice,
                    'response',
                    { username, deviceToken: deviceTokenFromLocalStorage || '', ...transaction },
                    'failed'
                );
            else
                await addLogEvent(
                    OKTA_LOGIN_URL + 'factors/' + factor?.id + '/verify?rememberDevice=' + rememberDevice,
                    'response',
                    { username, deviceToken: deviceTokenFromLocalStorage || '' },
                    'success'
                );

            const user: any = await getLoginUserInformation(dispatch, getState);

            if (!user) {
                return rejectWithValue('User not allowed to access this application');
            }

            return { status: mfaTransaction.status, user };
        } catch (error) {
            console.log(error);
            await addLogEvent(
                OKTA_LOGIN_URL + 'factors/' + factor?.id + '/verify?rememberDevice=' + rememberDevice,
                'response',
                { username, deviceToken: deviceTokenFromLocalStorage || '', error },
                'failed'
            );
            dispatch(logEventToBackEnd('CONFIRM_MFA_API_ERROR'));
            return rejectWithValue(error);
        }
    }
);

export const forgotPassword = async (email: string) => {
    try {
        const response = await uipApiInstance({
            method: 'POST',
            url: '/api/advice/forgotpassword',
            withCredentials: false,
            data: {
                email
            }
        });
        return response?.data?.success || response;
    } catch (error) {
        console.log(error);
        return error;
    }
};

export const sendForgotPasswordLink = createAsyncThunk(
    'authState/sendForgotPasswordLink',
    async ({ email }: any, { rejectWithValue, dispatch }) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/forgotpassword',
                withCredentials: false,
                data: {
                    email
                }
            });

            return response.data?.success;
        } catch (error) {
            console.log(error);
            dispatch(logEventToBackEnd('FORGOT_PASSWORD_API_ERROR'));
            return rejectWithValue(error);
        }
    }
);

export const logout = createAction('authState/logout');

const authSlice = createSlice({
    name: 'authState',
    initialState,
    reducers: {
        toggleSessionExpiredModal: (state, action: PayloadAction<any>) => {
            state.showSessionExpiredModal = action.payload;
        },
        updateOktaAuthState: (state, action: PayloadAction<any>) => {
            state.isAuthenticated = action.payload.isAuthenticated;
            state.oktaState = action.payload.authState;
        },
        resetAuthReducer: () => initialState,
        updateUserInfo: (state, action: PayloadAction<any>) => {
            state.user = action.payload;
        },
        // Marks notifications as seen for a tenant by advancing the local read-marker so the
        // unread dot clears in the same session the panel is opened.
        updateLatestNotificationTimestamp: (state, action: PayloadAction<{ tenant: string; timestamp: string }>) => {
            const { tenant, timestamp } = action.payload;
            if (!tenant) return;
            if (!state.user.cwa) state.user.cwa = {};
            if (!state.user.cwa[tenant]) state.user.cwa[tenant] = {};
            state.user.cwa[tenant].latestNotificationTimestamp = timestamp;
        },
        setLanguageChangedBeforeLogin: (state, action: PayloadAction<boolean>) => {
            state.languageChangedBeforeLogin = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(logout, (state) => ({
            ...initialState,
            showSessionExpiredModal: state.showSessionExpiredModal
        })),
            builder.addCase(getLoggedInUserInfo.pending, (state) => {
                state.loading = true;
            }),
            builder.addCase(getLoggedInUserInfo.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.error = '';
            }),
            builder.addCase(getLoggedInUserInfo.rejected, (state, action) => {
                state.loading = false;
                state.user = {};
                state.error = action.error;
            }),
            builder.addCase(getUserRelatedInfo.pending, (state) => {
                state.loading = true;
            }),
            builder.addCase(getUserRelatedInfo.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.error = '';
            }),
            builder.addCase(getUserRelatedInfo.rejected, (state) => {
                state.loading = false;
                state.user = {};
            }),
            builder.addCase(updateHantzUserSettings.pending, (state) => {
                state.isHantzUserSettings = false;
            }),
            builder.addCase(updateHantzUserSettings.fulfilled, (state) => {
                state.isHantzUserSettings = true;
            }),
            builder.addCase(updateHantzUserSettings.rejected, (state, { error }) => {
                state.isHantzUserSettings = false;
                state.error = error;
            });
    }
});

export const {
    toggleSessionExpiredModal,
    resetAuthReducer,
    updateOktaAuthState,
    updateUserInfo,
    updateLatestNotificationTimestamp,
    setLanguageChangedBeforeLogin
} = authSlice.actions;

export default authSlice.reducer;
